/**
 * Financial Summary (monthly_financial_summary) dataset builders
 */

import {
  differenceInCalendarMonths,
  endOfMonth,
  endOfQuarter,
  format,
  parseISO,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';
import type { BudgetPeriodType } from '../../types/budget.types';
import { scaleBudgetAmount } from '../../utils/budget-period';
import type {
  BudgetPerformanceStatus,
  IncomeVsExpensesSection,
  MonthlyTrendRow,
  PeriodComparisonSection,
  ReportDateRange,
  ReportDateRangePreset,
} from '../../types/report.types';
import {
  buildReportingCategoryMetaMap,
  financialSummaryExpenseAmount,
  financialSummaryIncomeAmount,
  isFinancialSummaryExpenseOutflow,
  type ReportingCategoryRecord,
} from './reporting-transaction-flow';

export interface FinancialSummaryTransaction {
  id: string;
  date: string;
  amount: number;
  type: string | null;
  category_id: string | null;
}

export function buildIncomeVsExpensesSection(
  transactions: FinancialSummaryTransaction[],
  categories: ReportingCategoryRecord[] = []
): IncomeVsExpensesSection {
  const categoryMeta = buildReportingCategoryMetaMap(categories);
  let totalIncome = 0;
  let totalExpenses = 0;
  for (const tx of transactions) {
    totalIncome += financialSummaryIncomeAmount(tx, categoryMeta);
    totalExpenses += financialSummaryExpenseAmount(tx, categoryMeta);
  }
  const netPosition = totalIncome - totalExpenses;
  const savingsRate =
    totalIncome > 0 ? Math.round((netPosition / totalIncome) * 1000) / 10 : null;
  return { totalIncome, totalExpenses, netPosition, savingsRate };
}

export function buildMonthlyTrendWithComparison(
  transactions: FinancialSummaryTransaction[],
  categories: ReportingCategoryRecord[] = []
): MonthlyTrendRow[] {
  const categoryMeta = buildReportingCategoryMetaMap(categories);
  const byMonth = new Map<string, { income: number; expenses: number }>();
  for (const tx of transactions) {
    const monthKey = tx.date.slice(0, 7);
    const entry = byMonth.get(monthKey) ?? { income: 0, expenses: 0 };
    entry.income += financialSummaryIncomeAmount(tx, categoryMeta);
    entry.expenses += financialSummaryExpenseAmount(tx, categoryMeta);
    byMonth.set(monthKey, entry);
  }

  const sorted = Array.from(byMonth.entries()).sort(([a], [b]) => a.localeCompare(b));
  let prevIncome: number | null = null;
  let prevExpenses: number | null = null;
  let prevNet: number | null = null;

  return sorted.map(([key, values]) => {
    const net = values.income - values.expenses;
    const row: MonthlyTrendRow = {
      monthLabel: format(parseISO(`${key}-01`), 'MMM yyyy'),
      income: values.income,
      expenses: values.expenses,
      net,
    };
    if (prevIncome != null && prevExpenses != null && prevNet != null) {
      row.incomeChange = values.income - prevIncome;
      row.expenseChange = values.expenses - prevExpenses;
      row.netChange = net - prevNet;
    }
    prevIncome = values.income;
    prevExpenses = values.expenses;
    prevNet = net;
    return row;
  });
}

/** Inclusive day count between ISO dates. */
function inclusiveDayCount(startDate: string, endDate: string): number {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
}

export function resolvePriorEqualPeriod(period: ReportDateRange): {
  startDate: string;
  endDate: string;
  label: string;
} {
  const days = inclusiveDayCount(period.startDate, period.endDate);
  const priorEnd = subDays(parseISO(period.startDate), 1);
  const priorStart = subDays(priorEnd, days - 1);
  const startDate = format(priorStart, 'yyyy-MM-dd');
  const endDate = format(priorEnd, 'yyyy-MM-dd');
  return {
    startDate,
    endDate,
    label: `${format(priorStart, 'd MMM yyyy')} – ${format(priorEnd, 'd MMM yyyy')}`,
  };
}

const PRIOR_PERIOD_SHIFT_YEAR: ReportDateRangePreset[] = [
  'this_calendar_year',
  'last_calendar_year',
  'this_financial_year',
  'last_financial_year',
];

const PRIOR_PERIOD_SHIFT_MONTH: ReportDateRangePreset[] = ['this_month', 'last_month'];

const PRIOR_PERIOD_SHIFT_QUARTER: ReportDateRangePreset[] = ['this_quarter', 'last_quarter'];

function formatPriorPeriodLabel(startDate: string, endDate: string): string {
  return `${format(parseISO(startDate), 'd MMM yyyy')} – ${format(parseISO(endDate), 'd MMM yyyy')}`;
}

/** Prior window aligned to the report preset (year-over-year for calendar/FY presets). */
export function resolvePriorComparisonPeriod(period: ReportDateRange): {
  startDate: string;
  endDate: string;
  label: string;
} {
  if (period.preset === 'custom') {
    return resolvePriorEqualPeriod(period);
  }

  const start = parseISO(period.startDate);
  const end = parseISO(period.endDate);

  if (PRIOR_PERIOD_SHIFT_YEAR.includes(period.preset)) {
    const startDate = format(subYears(start, 1), 'yyyy-MM-dd');
    const endDate = format(subYears(end, 1), 'yyyy-MM-dd');
    return { startDate, endDate, label: formatPriorPeriodLabel(startDate, endDate) };
  }

  if (PRIOR_PERIOD_SHIFT_MONTH.includes(period.preset)) {
    const priorAnchor = subMonths(end, 1);
    const startDate = format(startOfMonth(priorAnchor), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(priorAnchor), 'yyyy-MM-dd');
    return { startDate, endDate, label: formatPriorPeriodLabel(startDate, endDate) };
  }

  if (PRIOR_PERIOD_SHIFT_QUARTER.includes(period.preset)) {
    const priorAnchor = subQuarters(end, 1);
    const startDate = format(startOfQuarter(priorAnchor), 'yyyy-MM-dd');
    const endDate = format(endOfQuarter(priorAnchor), 'yyyy-MM-dd');
    return { startDate, endDate, label: formatPriorPeriodLabel(startDate, endDate) };
  }

  return resolvePriorEqualPeriod(period);
}

export function buildPeriodComparison(
  currentPeriod: ReportDateRange,
  currentTransactions: FinancialSummaryTransaction[],
  priorTransactions: FinancialSummaryTransaction[],
  categories: ReportingCategoryRecord[] = []
): PeriodComparisonSection {
  const current = buildIncomeVsExpensesSection(currentTransactions, categories);
  const prior = buildIncomeVsExpensesSection(priorTransactions, categories);
  const priorMeta = resolvePriorComparisonPeriod(currentPeriod);
  return {
    priorPeriodLabel: priorMeta.label,
    currentTotalIncome: current.totalIncome,
    currentTotalExpenses: current.totalExpenses,
    currentNetPosition: current.netPosition,
    priorTotalIncome: prior.totalIncome,
    priorTotalExpenses: prior.totalExpenses,
    priorNetPosition: prior.netPosition,
    incomeChange: current.totalIncome - prior.totalIncome,
    expenseChange: current.totalExpenses - prior.totalExpenses,
    netChange: current.netPosition - prior.netPosition,
  };
}

function resolveTopLevelCategoryName(
  categoryId: string,
  categories: ReportingCategoryRecord[],
  categoryMap: Map<string, { name: string }>
): string {
  const byId = new Map(categories.map((c) => [c.id, c]));
  let current = byId.get(categoryId);
  if (!current) return 'Unknown category';
  while (current.parent_id) {
    const parent = byId.get(current.parent_id);
    if (!parent) break;
    current = parent;
  }
  const topName = categoryMap.get(current.id)?.name;
  if (topName) return topName;
  const directName = categoryMap.get(categoryId)?.name;
  return directName ?? 'Unknown category';
}

export function formatFinancialSummaryDateRangeLabel(period: ReportDateRange): string {
  return `${format(parseISO(period.startDate), 'd MMM yyyy')} – ${format(parseISO(period.endDate), 'd MMM yyyy')}`;
}

function financialYearShortLabel(startDate: string): string {
  const startYear = parseISO(startDate).getFullYear();
  const endShort = (startYear + 1).toString().slice(-2);
  return `FY${startYear}–${endShort}`;
}

/** PDF title including period, e.g. "2025 Financial Summary" or "FY2024–25 Financial Summary". */
export function formatFinancialSummaryPdfTitle(period: ReportDateRange): string {
  if (
    period.preset === 'last_calendar_year' ||
    period.preset === 'this_calendar_year'
  ) {
    return `${format(parseISO(period.startDate), 'yyyy')} Financial Summary`;
  }
  if (
    period.preset === 'last_financial_year' ||
    period.preset === 'this_financial_year'
  ) {
    return `${financialYearShortLabel(period.startDate)} Financial Summary`;
  }
  return `Financial Summary — ${formatFinancialSummaryDateRangeLabel(period)}`;
}

function budgetStatusFromPercentUsed(percentUsed: number): BudgetPerformanceStatus {
  if (percentUsed > 100) return 'Over';
  if (percentUsed >= 90) return 'Near limit';
  return 'On track';
}

export function monthsSpannedInclusive(startDate: string, endDate: string): number {
  return differenceInCalendarMonths(parseISO(endDate), parseISO(startDate)) + 1;
}

function budgetPeriodLabel(periodType: BudgetPeriodType): string {
  if (periodType === 'weekly') return 'per week';
  if (periodType === 'yearly') return 'per year';
  return 'per month';
}

export function buildFinancialSummaryExpenseByCategory(
  transactions: FinancialSummaryTransaction[],
  categoryMap: Map<string, { name: string }>,
  categories: ReportingCategoryRecord[] = []
): Array<{ categoryName: string; amount: number; transactionCount: number; percent: number }> {
  const categoryMeta = buildReportingCategoryMetaMap(categories);
  const totals = new Map<string, { amount: number; count: number }>();
  for (const tx of transactions) {
    if (!isFinancialSummaryExpenseOutflow(tx, categoryMeta)) continue;
    let name: string;
    if (!tx.category_id) {
      name = 'Uncategorised';
    } else if (!categoryMap.has(tx.category_id)) {
      name = 'Unknown category';
    } else {
      name = resolveTopLevelCategoryName(tx.category_id, categories, categoryMap);
    }
    const entry = totals.get(name) ?? { amount: 0, count: 0 };
    entry.amount += financialSummaryExpenseAmount(tx, categoryMeta);
    entry.count += 1;
    totals.set(name, entry);
  }
  const total = Array.from(totals.values()).reduce((a, b) => a + b.amount, 0) || 1;
  return Array.from(totals.entries())
    .map(([categoryName, { amount, count }]) => ({
      categoryName,
      amount,
      transactionCount: count,
      percent: (amount / total) * 100,
    }))
    .sort((a, b) => b.amount - a.amount);
}

export function buildBudgetPerformanceFromTransactions(
  transactions: FinancialSummaryTransaction[],
  limits: Array<{ category_id: string; amount: number | string; period_type: BudgetPeriodType }>,
  categoryMap: Map<string, { name: string }>,
  categories: ReportingCategoryRecord[] = [],
  reportPeriod: ReportDateRange
): Array<{
  categoryName: string;
  budget: number;
  budgetPeriodLabel: string;
  totalSpent: number;
  averageSpentPerMonth: number;
  periodBudgetTotal: number;
  remaining: number;
  percentUsed: number;
  status: BudgetPerformanceStatus;
  periodVariance: number;
}> {
  const categoryMeta = buildReportingCategoryMetaMap(categories);
  const spentByCategory = new Map<string, number>();
  for (const tx of transactions) {
    if (!tx.category_id || !isFinancialSummaryExpenseOutflow(tx, categoryMeta)) continue;
    const amt = financialSummaryExpenseAmount(tx, categoryMeta);
    spentByCategory.set(tx.category_id, (spentByCategory.get(tx.category_id) ?? 0) + amt);
  }

  const months = Math.max(1, monthsSpannedInclusive(reportPeriod.startDate, reportPeriod.endDate));

  return limits.map((limit) => {
    const categoryName = categoryMap.get(limit.category_id)?.name ?? 'Unknown category';
    const budgetPerPeriod = Number(limit.amount) || 0;
    const periodType = limit.period_type;
    const monthlyBudget = scaleBudgetAmount(budgetPerPeriod, periodType, 'monthly');
    const periodBudgetTotal = monthlyBudget * months;
    const totalSpent = spentByCategory.get(limit.category_id) ?? 0;
    const averageSpentPerMonth = totalSpent / months;
    const remaining = periodBudgetTotal - totalSpent;
    const percentUsed =
      periodBudgetTotal > 0 ? (totalSpent / periodBudgetTotal) * 100 : totalSpent > 0 ? 100 : 0;
    return {
      categoryName,
      budget: monthlyBudget,
      budgetPeriodLabel: budgetPeriodLabel('monthly'),
      totalSpent,
      averageSpentPerMonth,
      periodBudgetTotal,
      remaining,
      percentUsed: Math.round(percentUsed * 10) / 10,
      status: budgetStatusFromPercentUsed(percentUsed),
      periodVariance: remaining,
    };
  });
}
